// AssignedMembers.tsx (Minor update to Autocomplete onChange)
import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
// ... (Interface Definitions Unchanged) ...

interface UserOption {
  value: string; // user ID
  username: string; // user_name
  id: string;
}

interface AssignedMember {
  id: string; // ID of the assignment record
  user: string; // User ID
  username: string; // Display Name
  initials: string; // Calculated initials
  bg: string; // Display color
  color: string; // Display text color
  isCreatedBy?: boolean; // Flag to indicate if this is the "created by" user
}

interface AssignedMembersProps {
  assignedMembers: AssignedMember[];
  usersDropDown?: UserOption[];
  loadingUsers?: boolean;
  isAssigning?: boolean;
  showAssignDropdown?: boolean;
  selectedUserToAssign?: UserOption | null;
  setShowAssignDropdown?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUserToAssign?: React.Dispatch<
    React.SetStateAction<UserOption | null>
  >;
  handleRemoveMember?: (
    assignmentId: string,
    memberName: string
  ) => Promise<void>;
  handleAssignMember?: () => Promise<void>;
}

const AssignedMembers: React.FC<AssignedMembersProps> = ({
  assignedMembers,
  usersDropDown,
  loadingUsers,
  isAssigning,
  showAssignDropdown,
  selectedUserToAssign,
  setShowAssignDropdown,
  setSelectedUserToAssign,
  handleRemoveMember,
  handleAssignMember,
}) => {
  // Ensure assignedMembers is always an array
  const membersArray = Array.isArray(assignedMembers) ? assignedMembers : [];

  // Helper function to extract initials from a full name string
  const getInitialsFromName = (name: string): string => {
    if (!name) return "";
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      // If we have at least 2 words, take first letter of first and last word
      return (
        (nameParts[0][0] || "") + (nameParts[nameParts.length - 1][0] || "")
      ).toUpperCase();
    } else if (nameParts.length === 1) {
      // If only one word, take first two letters
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return "";
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {membersArray.map((m) => {
          // Calculate initials: use provided initials, or extract from username if missing/incomplete
          const displayInitials =
            m.initials && m.initials.length >= 2
              ? m.initials
              : getInitialsFromName(m?.username || (m as any).user_name || "");

          return (
            <span
              key={m.id}
              className="px-2 py-1 rounded-pill fw-medium d-flex align-items-center gap-2"
              style={{ background: m.bg, color: m.color, fontSize: "12px" }}
            >
              {/* 🔹 Avatar */}
              <span
                className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                style={{
                  width: 28,
                  height: 28,
                  background: m.color,
                  color: m.bg,
                  fontSize: "11px",
                  letterSpacing: "0.5px",
                  flexShrink: 0,
                }}
              >
                {displayInitials}
              </span>

              {/* 🔹 Name */}
              <span className="text-nowrap">
                {m.isCreatedBy ? (
                  <>
                    <span style={{ color: "#6c757d", fontSize: "11px" }}>
                      Created By:{" "}
                    </span>
                    {m?.username || (m as any).user_name}
                  </>
                ) : (
                  m?.username || (m as any).user_name
                )}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default AssignedMembers;
